import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { interactiveLearningService } from '../../services/interactiveLearningService';
import './InteractiveLearningManagement.css';

const SUPPORTED_LANGUAGES = [
  { value: 'python', label: 'Python', icon: '🐍' },
  { value: 'javascript', label: 'JavaScript', icon: '🟨' },
  { value: 'java', label: 'Java', icon: '☕' },
  { value: 'cpp', label: 'C++', icon: '⚙️' },
  { value: 'html', label: 'HTML', icon: '🌐' },
  { value: 'css', label: 'CSS', icon: '🎨' },
  { value: 'sql', label: 'SQL', icon: '🗄️' },
  { value: 'other', label: 'Other', icon: '💻' }
];

const InteractiveLearningManagement = () => {
  const { courses, loading: dataLoading } = useData();
  const location = useLocation();
  
  // Initialize from navigation state if available
  const initialState = location.state || {};
  const [selectedCourse, setSelectedCourse] = useState(initialState.courseId || '');
  const [selectedTopic, setSelectedTopic] = useState(initialState.topicName || '');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [codingExamples, setCodingExamples] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newExample, setNewExample] = useState({
    language: 'python',
    code: '',
    description: '',
    enabled: true,
    title: ''
  });

  // Get topics for selected course
  const topics = selectedCourse 
    ? (courses.find(c => c.id === selectedCourse)?.topics || [])
    : [];

  // Load existing content when course/topic changes
  useEffect(() => {
    const loadContent = async () => {
      if (!selectedCourse || !selectedTopic) {
        setCodingExamples([]);
        return;
      }

      try {
        setLoading(true);
        const content = await interactiveLearningService.getTopicContent(selectedCourse, selectedTopic);
        
        // Support both old format (single coding) and new format (array)
        if (content?.codingExamples && Array.isArray(content.codingExamples)) {
          // New format: array of examples
          setCodingExamples(content.codingExamples);
        } else if (content?.coding) {
          // Old format: single coding object - convert to array
          setCodingExamples([{
            language: content.coding.language || 'python',
            code: content.coding.code || '',
            description: content.coding.description || '',
            enabled: content.coding.enabled !== undefined ? content.coding.enabled : true,
            title: content.coding.title || 'Code Example 1'
          }]);
        } else {
          setCodingExamples([]);
        }
      } catch (error) {
        console.error('Error loading content:', error);
        setCodingExamples([]);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [selectedCourse, selectedTopic]);

  // Handle navigation state
  useEffect(() => {
    if (location.state) {
      const { courseId, topicName } = location.state;
      if (courseId) {
        setSelectedCourse(courseId);
        if (topicName) {
          setSelectedTopic(topicName);
        }
      }
    }
  }, [location.state]);

  const handleSave = async () => {
    if (!selectedCourse || !selectedTopic) {
      alert('Please select a course and topic first.');
      return;
    }

    if (codingExamples.length === 0) {
      alert('Please add at least one code example before saving.');
      return;
    }

    try {
      setSaving(true);
      await interactiveLearningService.saveTopicContent(selectedCourse, selectedTopic, {
        codingExamples: codingExamples
      });
      alert('✅ Coding examples saved successfully!');
      setEditingIndex(null);
      setNewExample({ language: 'python', code: '', description: '', enabled: true, title: '' });
    } catch (error) {
      console.error('Error saving content:', error);
      alert('❌ Failed to save content. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddExample = () => {
    // Debug: Log current state
    console.log('Adding example:', { 
      title: newExample.title, 
      code: newExample.code, 
      hasTitle: !!newExample.title,
      hasCode: !!newExample.code
    });
    
    if (!newExample.title || !newExample.title.trim()) {
      alert('Please provide a title for the example. The title field should be at the top of the form.');
      return;
    }
    if (!newExample.code || !newExample.code.trim()) {
      alert('Please provide code for the example.');
      return;
    }
    
    const example = {
      ...newExample,
      id: Date.now().toString() // Simple ID generation
    };
    
    setCodingExamples(prev => [...prev, example]);
    setNewExample({ language: 'python', code: '', description: '', enabled: true, title: '' });
  };

  const handleEditExample = (index) => {
    setEditingIndex(index);
    setNewExample({ ...codingExamples[index] });
  };

  const handleUpdateExample = () => {
    if (editingIndex === null) return;
    
    const updated = [...codingExamples];
    updated[editingIndex] = { ...newExample, id: codingExamples[editingIndex].id || Date.now().toString() };
    setCodingExamples(updated);
    setEditingIndex(null);
    setNewExample({ language: 'python', code: '', description: '', enabled: true, title: '' });
  };

  const handleDeleteExample = (index) => {
    if (window.confirm('Are you sure you want to delete this code example?')) {
      setCodingExamples(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setNewExample({ language: 'python', code: '', description: '', enabled: true, title: '' });
  };

  const selectedLanguage = SUPPORTED_LANGUAGES.find(lang => lang.value === newExample.language);

  return (
    <div className="interactive-learning-management">
      <div className="page-header">
        <h1>💻 Coding Content Studio</h1>
        <p className="page-description">
          Create and manage coding content for course topics. Set the programming language, add code examples, and provide descriptions.
        </p>
      </div>
      
      <div className="course-topic-selector">
        <div className="selector-card">
          <h3>📚 Select Course & Topic</h3>
          <div className="selector-grid">
            <div className="form-group">
              <label>Course</label>
              <select
                value={selectedCourse}
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                  setSelectedTopic('');
                }}
                disabled={dataLoading}
              >
                <option value="">Choose a course...</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </select>
            </div>

            {selectedCourse && (
              <div className="form-group">
                <label>Topic</label>
                <select
                  value={selectedTopic}
                  onChange={(e) => setSelectedTopic(e.target.value)}
                >
                  <option value="">Choose a topic...</option>
                  {topics.map((topic, idx) => (
                    <option key={idx} value={topic.name}>
                      {topic.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedCourse && selectedTopic && (() => {
        const course = courses.find(c => c.id === selectedCourse);
        const topic = course?.topics?.find(t => t.name === selectedTopic);
        return topic ? (
          <div className="topic-info-banner">
            <div className="topic-info-content">
              <div className="topic-info-header">
                <h3>{topic.name}</h3>
                {topic.hours && <span className="topic-hours">⏱️ {topic.hours}h</span>}
              </div>
              {topic.description && (
                <p className="topic-description">{topic.description}</p>
              )}
            </div>
          </div>
        ) : null;
      })()}

      {dataLoading && (
        <div className="loading-state">
          <p>Loading courses...</p>
        </div>
      )}

      {selectedCourse && selectedTopic && !loading && (
        <div className="coding-editor-container">
          <div className="coding-editor-card">
            <div className="editor-header">
              <h2>💻 Coding Examples ({codingExamples.length})</h2>
              <button 
                onClick={handleSave} 
                className="save-btn" 
                disabled={saving || codingExamples.length === 0}
              >
                {saving ? '💾 Saving...' : '💾 Save All Examples'}
              </button>
            </div>

            {/* Existing Examples List */}
            {codingExamples.length > 0 && (
              <div className="examples-list">
                <h3>📋 Existing Examples</h3>
                {codingExamples.map((example, index) => (
                  <div key={example.id || index} className="example-card">
                    <div className="example-card-header">
                      <div className="example-info">
                        <h4>{example.title || `Example ${index + 1}`}</h4>
                        <span className="example-language-badge">
                          {SUPPORTED_LANGUAGES.find(l => l.value === example.language)?.icon || '💻'} {example.language}
                        </span>
                        {example.enabled ? (
                          <span className="example-status enabled">✅ Enabled</span>
                        ) : (
                          <span className="example-status disabled">❌ Disabled</span>
                        )}
                      </div>
                      <div className="example-actions">
                        <button 
                          onClick={() => handleEditExample(index)}
                          className="edit-example-btn"
                          disabled={editingIndex !== null}
                        >
                          ✏️ Edit
                        </button>
                        <button 
                          onClick={() => handleDeleteExample(index)}
                          className="delete-example-btn"
                          disabled={editingIndex !== null}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                    {example.description && (
                      <p className="example-description">{example.description}</p>
                    )}
                    <div className="example-code-preview">
                      <pre><code>{example.code.substring(0, 100)}{example.code.length > 100 ? '...' : ''}</code></pre>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add/Edit Form */}
            <div className="add-edit-form">
              <h3>{editingIndex !== null ? `✏️ Editing Example ${editingIndex + 1}` : '➕ Add New Example'}</h3>
              
              <div className="coding-form">
                {/* Title Field - Required */}
                <div className="form-group" style={{ marginBottom: '24px', display: 'block' }}>
                  <label htmlFor="title-input" style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    marginBottom: '10px', 
                    fontWeight: '600', 
                    color: '#1e293b',
                    fontSize: '0.95rem'
                  }}>
                    <span className="label-icon" style={{ marginRight: '8px' }}>📌</span>
                    Example Title <span style={{ color: '#dc2626', marginLeft: '4px' }}>*</span>
                  </label>
                  <input
                    id="title-input"
                    type="text"
                    value={newExample.title || ''}
                    onChange={(e) => setNewExample(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Hello World, Variables Example, Function Demo..."
                    className="title-input"
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      border: '2px solid #e2e8f0', 
                      borderRadius: '10px',
                      fontSize: '1rem',
                      backgroundColor: 'white',
                      boxSizing: 'border-box',
                      display: 'block'
                    }}
                  />
                  <small className="form-hint" style={{ display: 'block', marginTop: '8px', color: '#64748b', fontSize: '0.85rem' }}>
                    Give this example a descriptive title
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="language-select">
                    <span className="label-icon">{selectedLanguage?.icon || '💻'}</span>
                    Programming Language
                  </label>
                  <select
                    id="language-select"
                    value={newExample.language}
                    onChange={(e) => setNewExample(prev => ({ ...prev, language: e.target.value }))}
                    className="language-select"
                  >
                    {SUPPORTED_LANGUAGES.map(lang => (
                      <option key={lang.value} value={lang.value}>
                        {lang.icon} {lang.label}
                      </option>
                    ))}
                  </select>
                </div>

              <div className="form-group">
                <label htmlFor="description-input">
                  <span className="label-icon">📝</span>
                  Description
                </label>
                <textarea
                  id="description-input"
                  value={newExample.description}
                  onChange={(e) => setNewExample(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what this code does, learning objectives, or instructions for students..."
                  rows="3"
                  className="description-textarea"
                />
                <small className="form-hint">
                  Provide context and learning goals for this coding example
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="code-input">
                  <span className="label-icon">💻</span>
                  Code *
                </label>
                <textarea
                  id="code-input"
                  value={newExample.code}
                  onChange={(e) => setNewExample(prev => ({ ...prev, code: e.target.value }))}
                  placeholder={`// Enter your ${selectedLanguage?.label || 'code'} here...\n\n`}
                  rows="15"
                  className="code-textarea"
                  spellCheck="false"
                />
                <small className="form-hint">
                  Write or paste your code example here. Students will be able to view and interact with this code.
                </small>
              </div>

              <div className="form-group">
                <label className="enable-toggle-label">
                  <input
                    type="checkbox"
                    checked={newExample.enabled}
                    onChange={(e) => setNewExample(prev => ({ ...prev, enabled: e.target.checked }))}
                    className="enable-checkbox"
                  />
                  <span className="toggle-label-text">
                    {newExample.enabled ? '✅ Enabled' : '❌ Disabled'}
                  </span>
                </label>
                <small className="form-hint">
                  Enable this example to make it visible to students
                </small>
              </div>

              <div className="form-actions">
                {editingIndex !== null ? (
                  <>
                    <button 
                      onClick={handleUpdateExample}
                      className="update-btn"
                    >
                      ✅ Update Example
                    </button>
                    <button 
                      onClick={handleCancelEdit}
                      className="cancel-btn"
                    >
                      ✕ Cancel
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={handleAddExample}
                    className="add-example-btn"
                  >
                    ➕ Add Example
                  </button>
                )}
              </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!selectedCourse && !dataLoading && (
        <div className="empty-state">
          <div className="empty-state-icon">📚</div>
          <h3>Select a Course & Topic</h3>
          <p>Choose a course and topic from the dropdowns above to start creating coding content.</p>
        </div>
      )}
    </div>
  );
};

export default InteractiveLearningManagement;
